import React, {useContext, useRef, useState} from 'react';
import { useApolloClient } from '@apollo/client';
import { ASSETS_WITH_COUNT } from './queries';
import {
  MainContainer, ContentContainer, PageTitle, SubNavContainer
} from '../../elements';
import DataTable from '@preflighttech/preflight-tables';
import { View, Text, Pressable } from 'react-native';
import { Link } from 'app/src/utils/routing';
import moment from 'moment';
import IndexFilters from './IndexFilters';
import DownloadCsvButton from 'app/src/elements/DownloadCsvButton';
import HorizontalScroll from 'app/src/elements/HorizontalScroll';
import useCustomizableColumns from
  'app/src/elements/CustomizableColumns/useCustomizableColumns';
import NewAsset from './new';
import Modal from '../../elements/Modal';
import elRed from '../../styles/colors';
import AuthContext from '../../contexts/AuthContext';
import { currency } from 'app/src/components/Sales/Process';

import {
  AssetsTabButtonContainer,
  AssetsTabButton,
  AssetsTabText
} from './elements';

import formattedLocation from './formattedLocation';

// Size the table to its content so wide column sets scroll instead of squeezing.
const TABLE_STYLES = { table: { width: 'max-content', minWidth: '100%' } };

const ASSETS_TABLE_KEY = 'assets.index';

const SEARCH_FILTER = 'search';

const UNSOLD_TAB = 'Unsold';
const SOLD_TAB = 'Sold';

const SellerAssets = () => {
  const { canAddAsset, user } = useContext(AuthContext);
  const [refetch, setRefetch] = useState();
  const [filters, setFilters] = useState({});
  const [showAddAssetModal, setShowAddAssetModal] = useState(false);
  const [activeAssetsTab, setActiveAssetsTab] = useState(UNSOLD_TAB);
  // A ref, not state: preflight-tables reports settings from its own render,
  // and only the download reads them.
  const tableSettings = useRef();

  const client = useApolloClient();

  const queryVariables = {
    filterColumns: [],
    filterNames: [],
    scopes: [activeAssetsTab.toLowerCase()],
  };

  const setFilter = (key, value) => {
    const currentFilters = {...filters};
    currentFilters[key] = value;
    setFilters(currentFilters);
  }

  Object.entries(filters).forEach(([key, value]) => {
    if (!value) { return; }

    if (SEARCH_FILTER === key) {
      queryVariables.search = value;
    } else {
      queryVariables.filterColumns.push(key);
      queryVariables.filterNames.push(value);
    }
  });

  // No page limit, so the CSV covers every match rather than the visible page.
  const fetchAllEntries = async () => {
    const [sort] = tableSettings.current?.order || [];

    const { data } = await client.query({
      query: ASSETS_WITH_COUNT,
      fetchPolicy: 'no-cache',
      variables: {
        ...queryVariables,
        limit: null,
        offset: 0,
        sort: sort?.key,
        sortDirection: sort?.direction,
      },
    });

    return data.assetsWithCount.entries;
  };

  const columns = [
    {
      key: 'vin',
      label: 'VIN',
      sort: 'asc',
      content: (entry) => {
        return (
          <View>
            <Link to={`/assets/${entry.entry.id}`}>
              {`${entry.entry.vin}`}
              <Text style={{color: elRed}}>
                {`${ entry.entry.status === 'Not Yet Ready' ?
                  ' - ' + entry.entry.status : ''
                }`}
              </Text>
            </Link>
          </View>
        )
      }
    },
    {
      key: 'clientNumber',
      label: user?.seller.clientNumberLabel,
    },
    {
      key: 'year',
      label: 'Year',
    },
    {
      key: 'make',
      label: 'Make',
    },
    {
      key: 'model',
      label: 'Model',
    },
    {
      key: 'correctedLotNumber',
      label: 'Lot #',
      sort: 'prevent',
    },
    {
      key: 'cachedTitleStatus',
      label: 'Title Status',
    },
    {
      key: 'location',
      label: 'Location',
      value: ({ entry }) => formattedLocation(entry),
    },
    {
      // Assignment drives whose notifications an asset reaches, which is not
      // the same question as who added it.
      key: 'assignedTo',
      label: 'Assigned To',
      sort: 'prevent',
      value: ({ entry }) => (
        (entry.assetSellerUsers || []).
          map(assetSellerUser => assetSellerUser.sellerUser?.fullName).
          filter(Boolean).
          join(', ') || 'Unassigned'
      ),
    },
    {
      key: 'createdBy',
      label: 'Created By',
      hidden: SOLD_TAB === activeAssetsTab,
      sort: 'prevent',
      value: ({ entry }) => (
        'SellerUser' === entry.createdByType ? entry.createdByName : ''
      )
    },
    {
      key: 'soldAmount',
      label: 'Sold Price',
      hidden: UNSOLD_TAB === activeAssetsTab,
      value: ({ value }) => currency(value),
    },
    {
      key: 'sellerNet',
      label: 'Net ACH to Seller',
      hidden: UNSOLD_TAB === activeAssetsTab,
      sort: 'prevent',
      value: ({ entry }) => currency(entry.soldSaleAsset?.sellerNet),
    },
    {
      key: 'uccExpiresOn',
      label: 'Eligible Sale Date',
      value: ({ entry }) => (
        entry.uccExpiresOn ? moment(entry.uccExpiresOn).format('M/D/YYYY') : ''
      ),
    },
    {
      key: 'functions',
      label: 'Functions',
      sort: 'prevent',
      csvExport: false,
      customizable: false,
      content: (entry) => {
        return (
          <View style={{display: 'inline-flex'}}>
            <Link
              to={`/assets/${entry.entry.id}`}
              style={{marginLeft: '10px'}}
            >
              View
            </Link>
          </View>
        )
      }
    }
  ]

  const { columns: tableColumns, picker, exportColumns } =
    useCustomizableColumns({
      tableKey: ASSETS_TABLE_KEY,
      columns,
    });

  return (
    <MainContainer>
      {getSubNavContents()}
      <ContentContainer>
        <PageTitle>
          <Text><b>All Assets</b></Text>
        </PageTitle>
        <AssetsTabButtonContainer>
          <AssetsTabButton
            onPress={() => setActiveAssetsTab(UNSOLD_TAB)}
            active={UNSOLD_TAB === activeAssetsTab}
          >
            <AssetsTabText active={UNSOLD_TAB === activeAssetsTab}>
              Unsold Assets
            </AssetsTabText>
          </AssetsTabButton>
          <AssetsTabButton
            onPress={() => setActiveAssetsTab(SOLD_TAB)}
            active={SOLD_TAB === activeAssetsTab}
          >
            <AssetsTabText active={SOLD_TAB === activeAssetsTab}>
              Sold Assets
            </AssetsTabText>
          </AssetsTabButton>
        </AssetsTabButtonContainer>
        <IndexFilters
          filters={filters}
          setFilter={setFilter}
          onClear={() => setFilters({})}
        />
        <HorizontalScroll pinnedControls>
          <DataTable.Gql
            setRefetch={setRefetch}
            setSettings={settings => { tableSettings.current = settings; }}
            query={ASSETS_WITH_COUNT}
            queryName='assetsWithCount'
            queryVariables={queryVariables}
            columns={tableColumns}
            pageLength={25}
            buttons={
              <>
                {picker}
                <DownloadCsvButton
                  columns={exportColumns}
                  fetchEntries={fetchAllEntries}
                  filePrefix="assets"
                />
              </>
            }
            lengthMenu={ [10, 25, 50, 'All'] }
            styles={TABLE_STYLES}
            disableSearch
            htmlTable
          />
        </HorizontalScroll>
      </ContentContainer>
    </MainContainer>
  )
  function getSubNavContents() {
    return (
      <SubNavContainer>
        {getLinks()}
      </SubNavContainer>
    )
    function getLinks() {
      return (
        <View style={{display: 'flex', flexDirection: 'row'}}>
          {canAddAsset &&
            <Pressable
              onPress={() => setShowAddAssetModal(true)}
            >
              <Text>+ Add Asset</Text>
            </Pressable>
          }
          {showAddAssetModal &&
            <Modal
              showModal={showAddAssetModal}
              setShowModal={setShowAddAssetModal}
              modalType={'Add Asset'}
            >
              <NewAsset
                setShowModal={setShowAddAssetModal}
                refetch={refetch}
              />
            </Modal>
          }
        </View>
      )
    }
  }
}

export default SellerAssets;
